import React, { useState } from "react";
import { Card, Segmented } from "../components.jsx";
import { useStore } from "../store.jsx";
import { clasificacionDelPuesto } from "../data.js";
import { TRAMOS_MINISTERIO, rangoDeTramo } from "../ministerio.js";

import { PageHead } from "./dashboard.jsx";

/* ============================================================
   Talention — Agrupaciones del modelo Ministerio
   Sustituye a Escalas: los 10 tramos oficiales con sus puestos y
   un mapa agrupación × área, departamento, centro o nivel. Solo
   entran los puestos con la valoración finalizada.
   ============================================================ */

const EJES_MAPA = [
  { value: "area", label: "Área" },
  { value: "departamento", label: "Departamento" },
  { value: "centro", label: "Centro de trabajo" },
  { value: "nivel", label: "Nivel jerárquico" },
];

const SIN_ASIGNAR = { clave: "sin-asignar", nombre: "Sin asignar", orden: Infinity };

/* Columna del mapa para un puesto. `orden` coloca los departamentos en su
   orden, los niveles por jerarquía y deja «Sin asignar» al final; los
   textos libres (área, centro) se ordenan alfabéticamente. */
function columnaDe(puesto, eje, state) {
  if (eje === "departamento") {
    const i = state.departments.findIndex(d => d.id === puesto.departmentId);
    return i < 0 ? SIN_ASIGNAR : { clave: `dep-${state.departments[i].id}`, nombre: state.departments[i].name, orden: i };
  }
  if (eje === "nivel") {
    const nivel = state.levels.find(l => l.id === puesto.levelId);
    return nivel ? { clave: `niv-${nivel.id}`, nombre: nivel.name, orden: nivel.order } : SIN_ASIGNAR;
  }
  const valor = (puesto[eje] || "").trim();
  return valor ? { clave: `${eje}-${valor}`, nombre: valor, orden: 0 } : SIN_ASIGNAR;
}

const chip = (p) => `${p.name} (${Math.round(p.points)})`;

function Agrupaciones() {
  const { state } = useStore();
  const [eje, setEje] = useState("departamento");

  const clasificados = state.positions
    .map(p => ({ puesto: p, clasificacion: clasificacionDelPuesto(p, state) }))
    .filter(x => x.clasificacion);
  const deTramo = (agrupacion) => clasificados.filter(x => x.clasificacion.id === agrupacion).map(x => x.puesto);

  const conColumna = clasificados.map(x => ({ ...x, columna: columnaDe(x.puesto, eje, state) }));
  const columnas = [...new Map(conColumna.map(x => [x.columna.clave, x.columna])).values()]
    .sort((a, b) => a.orden - b.orden || a.nombre.localeCompare(b.nombre, "es"));
  const tramosDesc = [...TRAMOS_MINISTERIO].reverse();

  return (
    <div className="stack" style={{ gap: 22 }}>
      <PageHead
        title="Agrupaciones"
        sub="Tramos oficiales del modelo Ministerio. Un puesto recibe su agrupación al finalizar su valoración."
      />

      <Card title="Tramos de agrupación">
        <div className="stack" style={{ gap: 6 }}>
          {TRAMOS_MINISTERIO.map(t => (
            <div key={t.agrupacion} className="surface-soft" style={{ display: "flex", gap: 12, alignItems: "center", padding: "10px 12px", flexWrap: "wrap" }}>
              <span className="badge" style={{ background: t.color + "22", color: t.color, minWidth: 104 }}>Agrupación {t.agrupacion}</span>
              <span className="tiny muted" style={{ minWidth: 190 }}>{rangoDeTramo(t)}</span>
              <div className="row" style={{ gap: 6, flexWrap: "wrap", flex: 1 }}>
                {deTramo(t.agrupacion).map(p => <span key={p.id} className="badge">{chip(p)}</span>)}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Mapa de puestos" action={<Segmented value={eje} onChange={setEje} options={EJES_MAPA} />}>
        {clasificados.length === 0 ? (
          <div className="surface" style={{ padding: 24, textAlign: "center" }}>
            <div className="muted">Aún no hay puestos con la valoración finalizada</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="t-table" aria-label="Mapa de puestos">
              <thead>
                <tr>
                  <th>Agrupación</th>
                  {columnas.map(c => <th key={c.clave}>{c.nombre}</th>)}
                </tr>
              </thead>
              <tbody>
                {tramosDesc.map(t => (
                  <tr key={t.agrupacion}>
                    <th scope="row">
                      <span className="badge" style={{ background: t.color + "22", color: t.color }}>{t.agrupacion}</span>
                    </th>
                    {columnas.map(c => (
                      <td key={c.clave} data-testid={`celda-${t.agrupacion}-${c.clave}`}>
                        <div className="stack" style={{ gap: 4 }}>
                          {conColumna
                            .filter(x => x.clasificacion.id === t.agrupacion && x.columna.clave === c.clave)
                            .map(x => <span key={x.puesto.id} className="tiny">{chip(x.puesto)}</span>)}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

export { Agrupaciones };
